/**
 * Utility functions for handling expression-based field mappings.
 * Supports migration from JMESPath to JSONata expressions based on feature flag.
 */

/**
 * Represents any valid JSON value.
 */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

/**
 * Whitelist of allowed JSONata functions for security.
 * Only these functions can be used in JSONata expressions.
 */
export const ALLOWED_JSONATA_FUNCTIONS = new Set([
  // Array functions
  "length",
  "sort",
  "reverse",
  "distinct",
  "sum",
  "average",
  "count",
  // String functions
  "string",
  "substring",
  "substringBefore",
  "substringAfter",
  "lowercase",
  "uppercase",
  "trim",
  "pad",
  "split",
  "join",
  "contains",
  // Type functions
  "number",
  "boolean",
  "type",
  "exists",
  // Math functions
  "floor",
  "ceil",
  "round",
  "abs",
  // Array processing
  "filter",
  // Numeric functions
  "max",
  "min",
  // Array functions (additional)
  "append",
  "shuffle",
  "zip",
  // Higher-order functions
  "map",
  "reduce",
  "single",
  // String functions (additional)
  "replace",
  "match",
  // Math functions (additional)
  "sqrt",
  "power",
  "random",
  // Object functions
  "keys",
  "lookup",
  "merge",
  "spread",
  "sift",
  "each",
  // Numeric formatting
  "formatNumber",
  "formatInteger",
  "parseInteger",
  "formatBase",
  // Encoding functions
  "base64encode",
  "base64decode",
  "encodeUrlComponent",
  "encodeUrl",
  "decodeUrlComponent",
  "decodeUrl",
  // Date/time functions
  "now",
  "millis",
  "toMillis",
  "fromMillis",
  "formatDateTime",
  "parseDateTime",
  "dateFormat",
  "year",
  "month",
  "day",
  "hour",
  "minute",
  "second",
  "dayOfWeek",
  "dayOfYear",
  "dateAdd",
  "dateDiff",
  "timezone",
  "toUTC",
  "fromUTC",
]);

/**
 * Check if a JSONata expression only uses allowed functions.
 * Returns false if any disallowed function references (e.g., $eval) are found.
 * @param expression - The JSONata expression to validate
 * @returns true if expression only uses allowed functions, false otherwise
 */
export function containsOnlyAllowedJsonataFunctions(expression: string): boolean {
  // Match all function references like $functionName (with or without parentheses)
  const functionRefRegex = /\$([a-zA-Z_][a-zA-Z0-9_]*)/g;
  let match;
  while ((match = functionRefRegex.exec(expression)) !== null) {
    const functionName = match[1];
    if (!ALLOWED_JSONATA_FUNCTIONS.has(functionName)) {
      return false;
    }
  }
  return true;
}

/**
 * Get expression value for displaying/reading based on the deprecation flag.
 *
 * When flag is ON:
 *   - Prefer advanced_mapping_expression if it exists
 *   - Fall back to jmes_path for backward compatibility with existing data
 * When flag is OFF:
 *   - Use jmes_path
 *
 * @param mapping - Object containing jmes_path and/or advanced_mapping_expression
 * @param isJmesPathDeprecationEnabled - Whether the JMESPath deprecation flag is enabled
 * @returns The appropriate expression value or null
 */
export function getExpressionValue(
  mapping: { jmes_path?: string; advanced_mapping_expression?: string },
  isJmesPathDeprecationEnabled: boolean,
): string | null {
  if (isJmesPathDeprecationEnabled) {
    // Prefer advanced_mapping_expression, but fall back to jmes_path for existing data
    return mapping.advanced_mapping_expression ?? mapping.jmes_path ?? null;
  }
  return mapping.jmes_path ?? null;
}

/**
 * Build request payload with the appropriate expression field based on the flag.
 * @param expression - The expression string to include in the payload
 * @param isJmesPathDeprecationEnabled - Whether the JMESPath deprecation flag is enabled
 * @returns Object with the appropriate expression field, or empty object if expression is null/empty
 */
export function buildExpressionPayload(
  expression: string | null,
  isJmesPathDeprecationEnabled: boolean,
): Record<string, string> {
  if (!expression) return {};
  return isJmesPathDeprecationEnabled
    ? { advanced_mapping_expression: expression }
    : { jmes_path: expression };
}

/**
 * Get the UI label for the expression type based on the flag.
 * @param isJmesPathDeprecationEnabled - Whether the JMESPath deprecation flag is enabled
 * @returns "JSONata" if flag is enabled, "JMESPath" otherwise
 */
export function getExpressionLabel(isJmesPathDeprecationEnabled: boolean): string {
  return isJmesPathDeprecationEnabled ? "JSONata" : "JMESPath";
}
